import { Edit, Save, Cancel, ArrowBack, AvTimer, ChevronRight } from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { FC, useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import { useHistory, useParams } from "react-router-dom";
import { Keyinformation } from "../../../../clients/project";
import { User } from "../../../../clients/userClient";
import { PrivilegeContext } from "../../../../contexts/PrivilegeContext";
import { ProjectContext } from "../../../../contexts/ProjectContext";
import { UserContext } from "../../../../contexts/UserContext";
import { PrivilegeEnum } from "../../../RolesScreen/PrivilegeEnum";
import TimesheetModal from "./TimesheetModal";
import theme from "../../../../assets/theming/theme";

const ProjectKeyinformation: FC = () => {
  const { keyinformation, updateKeyinformation } = useContext(ProjectContext);
  const { projectId } = useParams<{ projectId: string }>();
  const { checkForPrivilege } = useContext(PrivilegeContext);
  const { users } = useContext(UserContext);
  const [editMode, setEditMode] = useState(false);
  const [timesheetDialog, setTimeSheetDialog] = useState(false);
  const history = useHistory();

  const { register, handleSubmit, reset, getValues, control } = useForm<Keyinformation>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: keyinformation,
  });

  const getUserByUsername = (username: string): User | null => {
    if (username) return users.find(({ accountInfo }) => accountInfo.username === username) as User;
    return null;
  };

  useEffect(() => {
    reset(keyinformation);
  }, [keyinformation, reset]);

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 2 }} style={{ flex: 1 }}>
      <Box
        maxWidth="850px"
        component="form"
        onSubmit={() => {
          updateKeyinformation(keyinformation as Keyinformation);
        }}>
        <Grid container spacing={2}>
          <Grid item md={12}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <IconButton
                onClick={() => history.push({ pathname: `/project/${projectId}/` })}
                sx={{ "&:hover": { color: theme.palette.primary.main } }}>
                <ArrowBack color="primary" />
              </IconButton>
              <Breadcrumbs sx={{ ml: 2, color: "inherit" }} separator={<ChevronRight fontSize="small" />}>
                <Typography variant="h6" color="GrayText">
                  {getValues("projectDto.title")}
                </Typography>
                <Typography variant="h6">
                  <FormattedMessage id="ProjectKeyinformation.table.title" />
                </Typography>
              </Breadcrumbs>
              {editMode ? (
                <Typography variant="caption" color="error" sx={{ ml: 1, fontStyle: "italic", fontSize: "0.75rem" }}>
                  <FormattedMessage id="EmployeeScreen.createUserDialog.personalInformation.requiredFields" />
                </Typography>
              ) : null}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", ml: "auto", gap: 2 }}>
                <Button size="small" variant="contained" startIcon={<AvTimer />} onClick={() => setTimeSheetDialog(true)}>
                  Timesheets
                </Button>
                {checkForPrivilege(PrivilegeEnum.CAN_EDIT_KEYINFORMATION) ? (
                  <>
                    {!editMode ? (
                      <Box>
                        <IconButton sx={{ "&:hover": { color: theme.palette.primary.main } }} onClick={() => setEditMode(true)}>
                          <Edit />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box>
                        <IconButton
                          color="primary"
                          onClick={handleSubmit((keyinformation) => {
                            updateKeyinformation({
                              ...keyinformation,
                              contactPersonExtensionsDto: getUserByUsername(getValues("contactPersonExtensionsDto.accountInfo.username")),
                              contactPersonAccountingDto: getUserByUsername(getValues("contactPersonAccountingDto.accountInfo.username")),
                            });
                            setEditMode(false);
                          })}>
                          <Save />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => {
                            setEditMode(false);
                            reset(keyinformation);
                          }}>
                          <Cancel />
                        </IconButton>
                      </Box>
                    )}
                  </>
                ) : null}
              </Box>
            </Box>
          </Grid>
          <Grid item md={12}>
            <Card>
              <CardContent>
                <Grid container spacing={2}>
                  {/* Aktueller AP für Verlängerungen */}
                  <Grid item md={6}>
                    <FormControl fullWidth>
                      <InputLabel sx={{ left: -14, bottom: -4 }} id="contactPersonExtensionsDtoLabel">
                        <FormattedMessage id="ProjectKeyinformation.label.currentRenewal" />
                      </InputLabel>
                      <Controller
                        name="contactPersonExtensionsDto.accountInfo.username"
                        control={control}
                        defaultValue={getValues("contactPersonExtensionsDto.accountInfo.username") ?? ""}
                        render={({ field: { onChange, value } }) => (
                          <Select
                            value={value}
                            onChange={onChange}
                            labelId="contactPersonExtensionsDtoLabel"
                            variant="standard"
                            inputProps={{ readOnly: !editMode }}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 224,
                                },
                              },
                            }}
                            data-testid="ContactPersonExtensionsSelect">
                            <MenuItem value="">
                              <Typography sx={{ fontStyle: "italic" }}>
                                <FormattedMessage id="ProjectKeyinformation.label.noCurrentRenewal" />
                              </Typography>
                            </MenuItem>
                            <Divider />
                            {users.map((user) => (
                              <MenuItem key={user.id} value={user.accountInfo.username}>
                                {user.accountInfo.username}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>
                  </Grid>
                  {/* Link Projektverträge */}
                  <Grid item md={6}>
                    <TextField
                      {...register("urlProjectContracts")}
                      InputLabelProps={{ shrink: true, focused: false }}
                      inputProps={{ readOnly: !editMode }}
                      fullWidth
                      variant="standard"
                      name="urlProjectContracts"
                      label={<FormattedMessage id="ProjectKeyinformation.label.linkProjectContracts" />}
                    />
                  </Grid>
                  {/* Vermittlungsagentur  */}
                  <Grid item md={6}>
                    <TextField
                      {...register("placementAgency")}
                      InputLabelProps={{ shrink: true, focused: false }}
                      inputProps={{ readOnly: !editMode }}
                      fullWidth
                      variant="standard"
                      name="placementAgency"
                      label={<FormattedMessage id="ProjectKeyinformation.label.placementAgency" />}
                    />
                  </Grid>
                  {/* Crefo Ja/Nein */}
                  <Grid item md={6}>
                    <Divider variant="middle" orientation="vertical" flexItem />
                    <label>
                      <input type="checkbox" {...register("crefo")} name="crefo" disabled={!editMode} />
                      <FormattedMessage id="ProjectKeyinformation.label.crefo" />
                    </label>
                  </Grid>
                  {/* AP Vermittler */}
                  <Grid item md={6}>
                    <TextField
                      {...register("contactPersonIntermediary")}
                      InputLabelProps={{ shrink: true, focused: false }}
                      inputProps={{ readOnly: !editMode }}
                      fullWidth
                      variant="standard"
                      name="contactPersonIntermediary"
                      label={<FormattedMessage id="ProjectKeyinformation.label.agent" />}
                    />
                  </Grid>
                  {/* AP Buchhaltung */}
                  <Grid item md={6}>
                    <FormControl fullWidth>
                      <InputLabel sx={{ left: -14, bottom: -4 }} id="contactPersonAccountingDtoLabel">
                        <FormattedMessage id="ProjectKeyinformation.label.accounting" />
                      </InputLabel>
                      <Controller
                        name="contactPersonAccountingDto.accountInfo.username"
                        control={control}
                        defaultValue={getValues("contactPersonAccountingDto.accountInfo.username") ?? ""}
                        render={({ field: { onChange, value } }) => (
                          <Select
                            value={value}
                            onChange={onChange}
                            labelId="contactPersonAccountingDtoLabel"
                            variant="standard"
                            inputProps={{ readOnly: !editMode }}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 224,
                                },
                              },
                            }}
                            data-testid="ContactPersonAccountingSelect">
                            <MenuItem value="">
                              <Typography sx={{ fontStyle: "italic" }}>
                                <FormattedMessage id="ProjectKeyinformation.label.noAccounting" />
                              </Typography>
                            </MenuItem>
                            <Divider />
                            {users.map((user) => (
                              <MenuItem key={user.id} value={user.accountInfo.username}>
                                {user.accountInfo.username}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>
                  </Grid>
                  {/* Projektnummer/-name */}
                  <Grid item md={6}>
                    <TextField
                      {...register("projectNumber")}
                      InputLabelProps={{ shrink: true, focused: false }}
                      inputProps={{ readOnly: !editMode }}
                      fullWidth
                      variant="standard"
                      name="projectNumber"
                      label={<FormattedMessage id="ProjectKeyinformation.label.projectNumberName" />}
                    />
                  </Grid>
                  {/* Auftragsbeschreibung */}
                  <Grid item md={6}>
                    <TextField
                      {...register("contractDescription")}
                      InputLabelProps={{ shrink: true, focused: false }}
                      inputProps={{ readOnly: !editMode }}
                      fullWidth
                      variant="standard"
                      name="contractDescription"
                      label={<FormattedMessage id="ProjectKeyinformation.label.orderDescription" />}
                    />
                  </Grid>
                  {/* Aktives Projekt */}
                  <Grid item md={6}>
                    <Divider variant="middle" orientation="vertical" flexItem />
                    <label>
                      <input type="checkbox" {...register("active")} name="active" disabled={!editMode} />
                      <FormattedMessage id="ProjectKeyinformation.label.activeProject" />
                    </label>
                  </Grid>
                  {/* Vertragsdauer */}
                  <Grid item md={6}>
                    <TextField
                      {...register("contractDuration")}
                      InputLabelProps={{ shrink: true, focused: false }}
                      inputProps={{ readOnly: !editMode }}
                      fullWidth
                      variant="standard"
                      name="contractDuration"
                      label={<FormattedMessage id="ProjectKeyinformation.label.contractDuration" />}
                    />
                  </Grid>
                  {/* Offen PT vom (vorherigen) Projekt */}
                  <Grid item md={6}>
                    <TextField
                      {...register("openManDays")}
                      InputLabelProps={{ shrink: true, focused: false }}
                      inputProps={{ readOnly: !editMode }}
                      fullWidth
                      type="number"
                      variant="standard"
                      name="openManDays"
                      label={<FormattedMessage id="ProjectKeyinformation.label.openProjectDays" />}
                    />
                  </Grid>
                  {/* Stundensatz */}
                  <Grid item md={6}>
                    <TextField
                      {...register("hourRate")}
                      InputLabelProps={{ shrink: true, focused: false }}
                      inputProps={{ readOnly: !editMode }}
                      fullWidth
                      type="number"
                      variant="standard"
                      name="hourRate"
                      label={<FormattedMessage id="ProjectKeyinformation.label.hourlyRate" />}
                    />
                  </Grid>
                  {/* Remote */}
                  <Grid item md={6}>
                    <Divider variant="middle" orientation="vertical" flexItem />
                    <label>
                      <input type="checkbox" {...register("remote")} name="remote" disabled={!editMode} />
                      <FormattedMessage id="ProjectKeyinformation.label.remote" />
                    </label>
                  </Grid>
                  {/* Ziel Tagessatz */}
                  <Grid item md={6}>
                    <TextField
                      {...register("dailyRateTarget")}
                      InputLabelProps={{ shrink: true, focused: false }}
                      inputProps={{ readOnly: !editMode }}
                      fullWidth
                      type="number"
                      variant="standard"
                      name="dailyRateTarget"
                      label={<FormattedMessage id="ProjectKeyinformation.label.targetDailyRate" />}
                    />
                  </Grid>
                  {/* Std/PT */}
                  <Grid item md={6}>
                    <TextField
                      {...register("hourManDay")}
                      InputLabelProps={{ shrink: true, focused: false }}
                      inputProps={{ readOnly: !editMode }}
                      fullWidth
                      type="number"
                      variant="standard"
                      name="hourManDay"
                      label={<FormattedMessage id="ProjectKeyinformation.label.hours" />}
                    />
                  </Grid>
                  {/* Rechnung versenden an (Mail) */}
                  <Grid item md={6}>
                    <TextField
                      {...register("invoiceEmail")}
                      InputLabelProps={{ shrink: true, focused: false }}
                      inputProps={{ readOnly: !editMode }}
                      fullWidth
                      variant="standard"
                      name="invoiceEmail"
                      label={<FormattedMessage id="ProjectKeyinformation.label.invoiceMail" />}
                    />
                  </Grid>
                  {/* Rechnungsadresse */}
                  <Grid item md={6}>
                    <TextField
                      {...register("billingAddress")}
                      InputLabelProps={{ shrink: true, focused: false }}
                      inputProps={{ readOnly: !editMode }}
                      fullWidth
                      variant="standard"
                      name="billingAddress"
                      label={<FormattedMessage id="ProjectKeyinformation.label.billingAddress" />}
                    />
                  </Grid>
                  {/* Gutschriftenverfahren */}
                  <Grid item md={6}>
                    <TextField
                      {...register("creditNotesProcedure")}
                      InputLabelProps={{ shrink: true, focused: false }}
                      inputProps={{ readOnly: !editMode }}
                      fullWidth
                      variant="standard"
                      name="creditNotesProcedure"
                      label={<FormattedMessage id="ProjectKeyinformation.label.creditMethod" />}
                    />
                  </Grid>

                  {/* Zahlungsziel */}
                  <Grid item md={6}>
                    <TextField
                      {...register("invoiceTarget")}
                      InputLabelProps={{ shrink: true, focused: false }}
                      inputProps={{ readOnly: !editMode }}
                      fullWidth
                      variant="standard"
                      name="invoiceTarget"
                      label={<FormattedMessage id="ProjectKeyinformation.label.paymentTarget" />}
                    />
                  </Grid>
                  {/* Besonderheiten */}
                  <Grid item md={6}>
                    <TextField
                      {...register("features")}
                      InputLabelProps={{ shrink: true, focused: false }}
                      inputProps={{ readOnly: !editMode }}
                      fullWidth
                      variant="standard"
                      name="features"
                      label={<FormattedMessage id="ProjectKeyinformation.label.features" />}
                    />
                  </Grid>
                  {/* Weg der Rechnungsstellung */}
                  <Grid item md={6}>
                    <TextField
                      {...register("invoiceMethod")}
                      InputLabelProps={{ shrink: true, focused: false }}
                      inputProps={{ readOnly: !editMode }}
                      fullWidth
                      variant="standard"
                      name="invoiceMethod"
                      label={<FormattedMessage id="ProjectKeyinformation.label.wayOfBilling" />}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      <TimesheetModal openDialog={timesheetDialog} setOpenDialog={setTimeSheetDialog} />
    </Container>
  );
};

export default ProjectKeyinformation;
